import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, XCircle, RefreshCw, LogOut, Clock, User, Calendar } from 'lucide-react'
import { Dialog, ModernButton, Toast, ToastType } from '../../../components/ui'
import { apiService, ApiError } from '../../../services/api'

interface MercadoLivreStatus {
  connected: boolean
  userId?: string
  lastSyncAt?: string | null
  lastError?: string | null
  configuredAt?: string
  message?: string
}

interface MercadoLivreInfoDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export const MercadoLivreInfoDialog: React.FC<MercadoLivreInfoDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()

  const [status, setStatus] = useState<MercadoLivreStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [reconnecting, setReconnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  useEffect(() => {
    if (isOpen) {
      void loadStatus()
    }
  }, [isOpen])

  const loadStatus = async (): Promise<void> => {
    try {
      setLoading(true)
      const statusData = await apiService.getMercadoLivreStatus()
      setStatus(statusData)
    } catch {
      setToast({
        message: t('marketplace.integrations.mercadolivre.errors.load'),
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReconnect = async (): Promise<void> => {
    try {
      setReconnecting(true)
      // The backend OAuth callback finishes the flow with a full page
      // redirect back to /marketplace?oauth=..., so this navigates the
      // current tab rather than opening a popup.
      const { url: oauthUrl } = await apiService.getMercadoLivreOAuthUrl()
      window.location.href = oauthUrl
    } catch (err: unknown) {
      const message =
        err instanceof ApiError
          ? err.message
          : t('marketplace.integrations.mercadolivre.errors.reconnect')
      setToast({
        message,
        type: 'error',
      })
    } finally {
      setReconnecting(false)
    }
  }

  const handleDisconnectClick = (): void => {
    setShowDisconnectConfirm(true)
  }

  const resolveDisconnectErrorMessage = (err: unknown): string => {
    if (err instanceof ApiError && err.status === 404) {
      return t('marketplace.integrations.mercadolivre.errors.notFound')
    }
    return t('marketplace.integrations.mercadolivre.errors.disconnect')
  }

  const handleDisconnectConfirm = async (): Promise<void> => {
    try {
      setDisconnecting(true)
      await apiService.disconnectMercadoLivre()
      setShowDisconnectConfirm(false)
      setToast({
        message: t('marketplace.success.disconnected'),
        type: 'success',
      })
      await loadStatus()
      onSuccess()
      window.setTimeout(() => {
        onClose()
      }, 1600)
    } catch (err: unknown) {
      setToast({
        message: resolveDisconnectErrorMessage(err),
        type: 'error',
      })
    } finally {
      setDisconnecting(false)
    }
  }

  const handleDisconnectCancel = (): void => {
    setShowDisconnectConfirm(false)
  }

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return t('common.never')
    return new Date(dateString).toLocaleString()
  }

  if (showDisconnectConfirm) {
    return (
      <>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <Dialog
          isOpen={isOpen}
          onClose={handleDisconnectCancel}
          title={t('marketplace.integrations.mercadolivre.disconnect.title')}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <XCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('marketplace.integrations.mercadolivre.disconnect.warning')}
                </p>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <ModernButton
                variant="outline"
                onClick={handleDisconnectCancel}
                disabled={disconnecting}
              >
                {t('actions.cancel')}
              </ModernButton>
              <ModernButton
                variant="danger"
                onClick={() => void handleDisconnectConfirm()}
                disabled={disconnecting}
                loading={disconnecting}
              >
                {disconnecting
                  ? t('marketplace.disconnecting')
                  : t('marketplace.disconnect')}
              </ModernButton>
            </div>
          </div>
        </Dialog>
      </>
    )
  }

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title={t('marketplace.integrations.mercadolivre.title')}
      >
        {loading ? (
          <div className="space-y-3">
            <div className="h-16 bg-muted animate-pulse rounded-lg" />
            <div className="h-12 bg-muted animate-pulse rounded-lg" />
            <div className="h-12 bg-muted animate-pulse rounded-lg" />
          </div>
        ) : !status?.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              <XCircle className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">
                  {t('marketplace.notConnected')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {status?.message || t('marketplace.integrations.mercadolivre.notConfigured')}
                </p>
              </div>
            </div>
            <ModernButton onClick={onClose} className="w-full">
              {t('actions.close')}
            </ModernButton>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-500" />
              <div>
                <p className="font-medium text-green-900 dark:text-green-100">
                  {t('marketplace.connected')}
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  {t('marketplace.integrations.mercadolivre.active')}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                {t('marketplace.integrations.mercadolivre.accountInfo')}
              </h4>

              {status.userId && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">
                      {t('marketplace.integrations.mercadolivre.userId')}
                    </p>
                    <p className="text-sm font-medium text-foreground">{status.userId}</p>
                  </div>
                </div>
              )}

              {status.configuredAt && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">
                      {t('marketplace.integrations.mercadolivre.connectedAt')}
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate(status.configuredAt)}
                    </p>
                  </div>
                </div>
              )}

              {status.lastSyncAt && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">
                      {t('marketplace.integrations.mercadolivre.lastSync')}
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate(status.lastSyncAt)}
                    </p>
                  </div>
                </div>
              )}

              {status.lastError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-red-700 dark:text-red-300 mb-1">
                      {t('marketplace.integrations.mercadolivre.lastError')}
                    </p>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {status.lastError}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <ModernButton
                variant="outline"
                onClick={() => void handleReconnect()}
                disabled={reconnecting || disconnecting}
                loading={reconnecting}
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {reconnecting
                  ? t('marketplace.reconnecting')
                  : t('marketplace.reconnect')}
              </ModernButton>
              <ModernButton
                variant="danger"
                onClick={handleDisconnectClick}
                disabled={reconnecting || disconnecting}
                className="flex-1"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('marketplace.disconnect')}
              </ModernButton>
            </div>
          </div>
        )}
      </Dialog>
    </>
  )
}
